// `TwoFactorSetup` — 2FA enrollment dialog (US-009, US-010).
//
// Better Auth's TOTP plugin returns:
//   - `secret`     : raw base32 string (manual-entry fallback).
//   - `totpURI`    : `otpauth://totp/...` URI we render as a QR code.
//   - `qrCodeDataUrl?` : optional PNG data-URL when BA renders one
//     server-side. We prefer it when present; otherwise we render the
//     URI in the manual-key field and the user can paste it into their
//     app of choice.
//   - `backupCodes`: array of 10 one-time codes. Wave B6 displays them
//     as a copyable list + .txt download, and offers a regenerate flow
//     once 2FA is active.
//
// State machine:
//   password → enroll → verify → backup-codes (optional) → close
//
// On disable, we POST `/two-factor/disable` with the current password
// and show a destructive confirm via Banner + button (no extra dialog).
// The QR is rendered as a `<canvas>` via the `qrCodeDataUrl` PNG
// (when available). DS-GAP: full QR-code primitive is out of scope —
// we inline an `<img>` with token-aware bg.

import { useState } from 'react';

import {
  ApiError,
  use2faDisable,
  use2faEnroll,
  use2faVerify,
  useRegenerateBackupCodes,
} from '@barel/api-client/react';
import { useT } from '@barel/i18n';
import {
  Banner,
  Button,
  Checkbox,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Field,
  Input,
  toast,
} from '@barel/ui';

export interface TwoFactorSetupProps {
  /** Whether 2FA is currently active for the user. */
  enabled: boolean;
}

interface EnrollData {
  secret: string;
  totpURI: string;
  qrCodeDataUrl?: string;
  backupCodes?: string[];
}

type Step = 'idle' | 'password' | 'verify' | 'backup' | 'regenerate';

export function TwoFactorSetup({ enabled }: TwoFactorSetupProps) {
  const t = useT('settings');
  const tCommon = useT('common');
  const enroll = use2faEnroll();
  const verify = use2faVerify();
  const disable = use2faDisable();
  const regenerate = useRegenerateBackupCodes();

  const [step, setStep] = useState<Step>('idle');
  const [password, setPassword] = useState<string>('');
  const [code, setCode] = useState<string>('');
  const [trustDevice, setTrustDevice] = useState<boolean>(false);
  const [enrollData, setEnrollData] = useState<EnrollData | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const close = (): void => {
    setStep('idle');
    setPassword('');
    setCode('');
    setTrustDevice(false);
    setEnrollData(null);
    setBackupCodes(null);
    setError(null);
  };

  const handleEnroll = async (): Promise<void> => {
    setError(null);
    try {
      const data = await enroll.mutateAsync({ password });
      setEnrollData(data);
      setStep('verify');
    } catch (err) {
      setError(
        ApiError.is(err) && err.code === 'auth.bad_credentials'
          ? t('security.passwordError')
          : t('security.twoFactorBadCode'),
      );
    }
  };

  const handleVerify = async (): Promise<void> => {
    setError(null);
    try {
      await verify.mutateAsync({ code, trustDevice });
      const codes = enrollData?.backupCodes ?? null;
      if (codes && codes.length > 0) {
        setBackupCodes(codes);
        setStep('backup');
        toast.success({ title: t('security.twoFactorEnabledToast') });
      } else {
        toast.success({ title: t('security.twoFactorEnabledToast') });
        close();
      }
    } catch {
      setError(t('security.twoFactorBadCode'));
    }
  };

  const handleDisable = async (): Promise<void> => {
    setError(null);
    try {
      await disable.mutateAsync({ password });
      toast.success({ title: t('security.twoFactorDisabledToast') });
      close();
    } catch {
      setError(t('security.passwordError'));
    }
  };

  const handleRegenerate = async (): Promise<void> => {
    setError(null);
    try {
      const result = await regenerate.mutateAsync({ password });
      setBackupCodes(result.backupCodes);
      setStep('backup');
    } catch {
      setError(t('security.passwordError'));
    }
  };

  const open = step !== 'idle';

  return (
    <div data-component="TwoFactorSetup" className="flex flex-col gap-2">
      {!enabled ? (
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={() => setStep('password')}
          data-testid="settings-2fa-enable"
        >
          {t('security.twoFactorEnable')}
        </Button>
      ) : (
        <div className="flex flex-col items-end gap-2">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => setStep('password')}
            data-testid="settings-2fa-disable"
          >
            {t('security.twoFactorDisable')}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => setStep('regenerate')}
            data-testid="settings-2fa-regenerate"
          >
            {t('security.twoFactorRegenerate')}
          </Button>
        </div>
      )}

      <Dialog
        open={open}
        onOpenChange={(next: boolean) => {
          if (!next) close();
        }}
      >
        <DialogContent
          aria-label={enabled ? t('security.twoFactorDisable') : t('security.twoFactorEnable')}
        >
          <DialogHeader
            title={
              step === 'backup'
                ? t('security.twoFactorBackupTitle')
                : step === 'regenerate'
                  ? t('security.twoFactorRegenerate')
                  : enabled
                    ? t('security.twoFactorDisable')
                    : t('security.twoFactorEnable')
            }
            description={
              step === 'backup'
                ? t('security.twoFactorBackupHint')
                : step === 'regenerate'
                  ? t('security.twoFactorRegenerateConfirm')
                  : enabled
                    ? t('security.twoFactorHint')
                    : t('security.twoFactorScanHint')
            }
          />
          <DialogBody>
            {error ? (
              <Banner tone="error" className="mb-3">
                {error}
              </Banner>
            ) : null}

            {step === 'password' || step === 'regenerate' ? (
              <Field required>
                <Field.Label>{t('security.twoFactorPasswordLabel')}</Field.Label>
                <Field.Control>
                  <Input
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      setPassword(event.target.value)
                    }
                    data-testid="settings-2fa-password"
                  />
                </Field.Control>
              </Field>
            ) : null}

            {step === 'verify' && enrollData ? (
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-center rounded-lg border border-default bg-bg-elevated p-4">
                  {enrollData.qrCodeDataUrl ? (
                    <img
                      src={enrollData.qrCodeDataUrl}
                      width={196}
                      height={196}
                      alt={t('security.twoFactorScanHint')}
                      data-testid="settings-2fa-qr"
                    />
                  ) : (
                    <code
                      data-testid="settings-2fa-uri"
                      className="break-all text-xs text-secondary"
                    >
                      {enrollData.totpURI}
                    </code>
                  )}
                </div>
                <div className="flex flex-col gap-1 rounded-md bg-bg-elevated p-3 text-xs">
                  <span className="text-tertiary">{t('security.twoFactorManualHint')}</span>
                  <code data-testid="settings-2fa-secret" className="break-all text-primary">
                    {enrollData.secret}
                  </code>
                </div>
                <Field required>
                  <Field.Label>{t('security.twoFactorCodeLabel')}</Field.Label>
                  <Field.Control>
                    <Input
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      pattern="[0-9]*"
                      maxLength={6}
                      placeholder={t('security.twoFactorCodePlaceholder')}
                      value={code}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        setCode(event.target.value.replace(/\D/gu, ''))
                      }
                      data-testid="settings-2fa-code"
                    />
                  </Field.Control>
                </Field>
                <label className="inline-flex items-center gap-2 text-sm text-secondary">
                  <Checkbox
                    checked={trustDevice}
                    onCheckedChange={(value: boolean | 'indeterminate') =>
                      setTrustDevice(value === true)
                    }
                    data-testid="settings-2fa-trust"
                  />
                  <span>{t('security.twoFactorTrustDevice')}</span>
                </label>
              </div>
            ) : null}

            {step === 'backup' && backupCodes ? <BackupCodesPanel codes={backupCodes} /> : null}
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={close}>
              {tCommon('actions.cancel')}
            </Button>
            {step === 'password' ? (
              <Button
                type="button"
                variant="primary"
                loading={enroll.isPending || disable.isPending}
                onClick={() => {
                  if (enabled) {
                    void handleDisable();
                  } else {
                    void handleEnroll();
                  }
                }}
                data-testid="settings-2fa-submit"
              >
                {enabled ? t('security.twoFactorDisable') : t('security.twoFactorEnable')}
              </Button>
            ) : null}
            {step === 'regenerate' ? (
              <Button
                type="button"
                variant="primary"
                loading={regenerate.isPending}
                onClick={() => {
                  void handleRegenerate();
                }}
                data-testid="settings-2fa-regenerate-submit"
              >
                {t('security.twoFactorRegenerate')}
              </Button>
            ) : null}
            {step === 'verify' ? (
              <Button
                type="button"
                variant="primary"
                loading={verify.isPending}
                disabled={code.length < 6}
                onClick={() => {
                  void handleVerify();
                }}
                data-testid="settings-2fa-verify"
              >
                {t('security.twoFactorVerify')}
              </Button>
            ) : null}
            {step === 'backup' ? (
              <Button
                type="button"
                variant="primary"
                onClick={close}
                data-testid="settings-2fa-backup-done"
              >
                {tCommon('actions.cancel')}
              </Button>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface BackupCodesPanelProps {
  codes: readonly string[];
}

function BackupCodesPanel({ codes }: BackupCodesPanelProps) {
  const t = useT('settings');

  const onCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(codes.join('\n'));
      toast.success({ title: t('security.twoFactorBackupCopied') });
    } catch {
      // No clipboard permission — fall back silently; the codes are visible.
    }
  };

  const onDownload = (): void => {
    const blob = new Blob([codes.join('\n') + '\n'], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'barel-2fa-backup-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex flex-col gap-3" data-testid="settings-2fa-backup-codes">
      <ul className="grid grid-cols-2 gap-2 rounded-md border border-default bg-bg-elevated p-3 font-mono text-xs">
        {codes.map((codeLine) => (
          <li key={codeLine} className="break-all text-primary">
            {codeLine}
          </li>
        ))}
      </ul>
      <div className="flex gap-2">
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={() => {
            void onCopy();
          }}
          data-testid="settings-2fa-backup-copy"
        >
          {t('security.twoFactorBackupCopy')}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={onDownload}
          data-testid="settings-2fa-backup-download"
        >
          {t('security.twoFactorBackupDownload')}
        </Button>
      </div>
    </div>
  );
}
